import { Component, Input, SimpleChanges, forwardRef, inject, OnInit, OnChanges } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { Observable, BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, first, map, tap } from 'rxjs/operators';

import { BaseControlValueAndValidationAccessorComponent } from 'rev-shared/util/directives/form/validation/BaseControlValueAndValidationAccessor.Component';
import { DateUtil } from 'rev-shared/date/DateUtil';
import { IMediaFeatures } from 'rev-shared/media/IMediaFeatures';

import { IRtmpSettings } from './IRtmpSettings';
import { RtmpListenerUrlConflict, SecondaryRtmpListenerUrlConflict } from './EditWebcast.Service';
import { RtmpVideoSourceDetailsService, updateRtmpUrl } from './RtmpVideoSourceDeatils.Service';
import { WebcastVideoSource } from '../webcast/WebcastVideoSource';

@Component({
	selector: 'rtmp-video-source-details',
	templateUrl: './RtmpVideoSourceDetails.Component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => RtmpVideoSourceDetailsComponent),
			multi: true
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => RtmpVideoSourceDetailsComponent),
			multi: true
		}
	],
	standalone: false
})
export class RtmpVideoSourceDetailsComponent extends BaseControlValueAndValidationAccessorComponent<IRtmpSettings> implements OnInit, OnChanges {
	public readonly WebcastVideoSource = WebcastVideoSource;
	@Input() public readonly endDate: Date;
	@Input() public readonly issue: string;
	@Input() public readonly preProductionDurationMs: number;
	@Input() public readonly rtmpSettings: IRtmpSettings;
	@Input() public readonly startDate: Date;
	@Input() public readonly themed: boolean = false;
	@Input() public readonly webcastId: string;
	@Input() public readonly features: IMediaFeatures;
	@Input() public readonly webcastType: WebcastVideoSource;

	private readonly RtmpVideoSourceDetailsService = inject(RtmpVideoSourceDetailsService);
	private onChange: (value: IRtmpSettings) => void = () => { };
	private onTouched: () => void = () => {};
	private isFirstWrite = true;
	private readonly rtmpSettingsSubject$: BehaviorSubject<IRtmpSettings> = new BehaviorSubject<IRtmpSettings>({ isSecure: true, backupStreamEnabled: false });
	private backupStreamEnabledCopy: boolean = false;

	public errors: ValidationErrors = {};
	public rtmpContext$: Observable<IRtmpSettings>;
	public initRtmpContext$: Observable<IRtmpSettings> = this.rtmpSettingsSubject$
		.asObservable()
		.pipe(
			filter(() => !this.isFirstWrite),
			first(),
			map(urlDetails => {
				if (urlDetails.pushUrl) {
					urlDetails.pushUrl = updateRtmpUrl(urlDetails.pushUrl, urlDetails.isSecure, urlDetails.streamKey, this.webcastType);
				}
				if (urlDetails.backupStreamUrl) {
					urlDetails.backupStreamUrl = updateRtmpUrl(urlDetails.backupStreamUrl, urlDetails.isSecure, urlDetails.backupStreamKey, this.webcastType);
				}
				return urlDetails;
			}),
			tap(settings => {
				if (!settings?.pushUrl) {
					this.generateNewSettings(true, true, false);
				}
			})
		);

	public ngOnInit(): void {
		this.rtmpContext$ = this.rtmpSettingsSubject$
			.asObservable()
			.pipe(
				distinctUntilChanged(),
				tap(settings => {
					this.onChange(settings);
				})
			);

		//no need to unsubscribe becasye of first operator
		this.initRtmpContext$.subscribe();
	}

	public writeValue(value: IRtmpSettings): void {
		if (this.isFirstWrite) {
			this.isFirstWrite = false;
			if (value == null) {
				return;
			}
		}
		this.setRtmpSettingsSubject(value);
	}

	public registerOnChange(fn: (value: IRtmpSettings) => void): void {
		this.onChange = fn;
	}

	public registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	public validate(): ValidationErrors {
		const notValid = Object.values(this.errors).some(Boolean);
		return notValid ? this.errors : undefined;
	}

	public ngOnChanges({ startDate, endDate, preProductionDurationMs, issue, webcastType }: SimpleChanges) {
		if (webcastType && !webcastType.firstChange) {
			this.generateNewSettings(true, true, true);
		}
		if (startDate || endDate || preProductionDurationMs) {
			const rtmpSettings = this.rtmpSettingsSubject$.value;
			if (!rtmpSettings) {
				return;
			}
			const { pushUrl, backupStreamUrl } = rtmpSettings;
			if (pushUrl) {
				this.validateVideoSourceSchedule(pushUrl, true);
			}
			if (backupStreamUrl) {
				this.validateVideoSourceSchedule(backupStreamUrl);
			}
		}

		if (issue && this.issue) {
			this.onHandleError({ [this.issue]: true });
		}
	}

	public onProtocolChange(isSecure: boolean): void {
		const settings = this.rtmpSettingsSubject$.value;

		if (!settings?.pushUrl) {
			return;
		}
		const newUrl = updateRtmpUrl(settings.pushUrl, isSecure, settings.streamKey, this.webcastType);
		const newBackupUrl = settings.backupStreamEnabled ? updateRtmpUrl(settings.backupStreamUrl, isSecure, settings.backupStreamKey, this.webcastType) : undefined;
		this.setRtmpSettingsSubject({ isSecure, pushUrl: newUrl, backupStreamUrl: newBackupUrl });
	}

	public onBackupStreamChange(enabled: boolean, isSecure: boolean): void {
		//temporary fix to avoid unnecessary calls to generateNewSettings
		//vbUiRadioBtn directive has click handler which keeps firing on every click
		if (this.backupStreamEnabledCopy === enabled) {
			return; // No change in backup stream enabled state
		}
		if(enabled) {
			this.generateNewSettings(isSecure, false, false);
		} else {
			this.setRtmpSettingsSubject({ backupStreamEnabled: false, backupStreamUrl: undefined, backupStreamKey: undefined });
		}
	}

	public generateNewSettings(isSecure: boolean, isPrimary: boolean, resetPrimarySettings: boolean): void {
		this.fetchNewRtmpUrlAndKey(isSecure)
			.pipe(
				first(),
				tap(settings => this.validateVideoSourceSchedule(settings.url, isPrimary))
			).subscribe(settings => {
				const rtmp = isPrimary ? {
					isSecure,
					pushUrl: settings.url,
					streamKey: settings.streamKey,
				} : {
					isSecure,
					backupStreamEnabled: true,
					backupStreamUrl: settings.url,
					backupStreamKey: settings.streamKey
				};
				if (resetPrimarySettings) {
					rtmp.backupStreamEnabled = false;
				}
				this.setRtmpSettingsSubject(rtmp);
			});
	}

	private setRtmpSettingsSubject(settings: Partial<IRtmpSettings>): void {
		const currentSettings = this.rtmpSettingsSubject$.value;
		const newSettings = {
			...currentSettings,
			...settings
		};
		this.backupStreamEnabledCopy = newSettings.backupStreamEnabled;
		this.rtmpSettingsSubject$.next(newSettings);
	}

	private fetchNewRtmpUrlAndKey(isSecure: boolean): Observable<{ url: string; streamKey: string }> {
		return this.RtmpVideoSourceDetailsService.getRtmpUrlDetails(isSecure, this.webcastType)
			.pipe(
				map(urlDetails => ({
					url: updateRtmpUrl(urlDetails.url, isSecure, urlDetails.streamKey, this.webcastType), streamKey: urlDetails.streamKey
				}))
			);
	}


	private validateVideoSourceSchedule(url: string, isPrimary?: boolean): void {
		if (!url || !(this.startDate instanceof Date) || !(this.endDate instanceof Date)) {
			return;
		}

		const startDate = DateUtil.addDays(this.getStartDate(), -1);
		const endDate = DateUtil.addDays(this.endDate, 1);

		this.loadSchedule(startDate, endDate, url, this.webcastType)
			.pipe(
				first(),
				map(events => {
					return (events || []).filter(event =>
						event.id !== this.webcastId &&
						event.endDate > this.getStartDate() &&
						event.startDate < this.endDate
					).length > 0;
				}),
				tap(invalid => {
					const key = isPrimary ? RtmpListenerUrlConflict : SecondaryRtmpListenerUrlConflict;
					this.onHandleError({ [key]: invalid });
				})
			).subscribe();

	}

	private loadSchedule(startDate: Date, endDate: Date, pushUrl: string, webcastType: WebcastVideoSource): Observable<any> {
		return this.RtmpVideoSourceDetailsService.getRtmpVideoSourceSchedules(startDate.toISOString(), endDate.toISOString(), pushUrl, webcastType);
	}

	private getStartDate(): Date {
		return this.preProductionDurationMs > 0 ?
			new Date(this.startDate.getTime() - this.preProductionDurationMs) :
			this.startDate;
	}

	private onHandleError(err: { [key: string]: boolean }): void {
		this.errors = {
			...this.errors,
			...err
		};
		this.form.control.updateValueAndValidity();
		this.onTouched();
	}
}
